import React from "react";
import "./featureTrailer.css";

import {
  Inventory2,
  PointOfSale,
  QrCodeScanner,
  LocalShipping,
  Assessment,
  CloudSync,
  NotificationsActive,
  Security,
  TrendingUp,
  AutoGraph,
  Storefront,
  ArrowOutward,
} from "@mui/icons-material";

const FEATURES = [
  {
    id: 1,
    category: "INVENTORY CONTROL",
    title: "Know Your Inventory",
    description:
      "Track products, quantities, stock movement and inventory value from one intelligent workspace.",
    icon: Inventory2,
    metric: "12.8K",
    metricLabel: "Items tracked",
    accent: "blue",
    preview: {
      label: "Inventory health",
      value: "94.8%",
      status: "Excellent",
    },
  },
  {
    id: 2,
    category: "SMART POS",
    title: "Sell Faster",
    description:
      "Turn every checkout into a seamless transaction with a powerful point-of-sale experience.",
    icon: PointOfSale,
    metric: "2.4s",
    metricLabel: "Avg. checkout",
    accent: "green",
    preview: {
      label: "Today's sales",
      value: "₦842K",
      status: "+18.4%",
    },
  },
  {
    id: 3,
    category: "BARCODE & QR",
    title: "Scan. Find. Move.",
    description:
      "Instantly identify products and accelerate receiving, selling, transfers and stock operations.",
    icon: QrCodeScanner,
    metric: "98.6%",
    metricLabel: "Scan accuracy",
    accent: "purple",
    preview: {
      label: "Latest scan",
      value: "SKU-28491",
      status: "Verified",
    },
  },
  {
    id: 4,
    category: "DELIVERY",
    title: "Every Order, Visible",
    description:
      "Monitor deliveries from preparation to completion without losing sight of your inventory.",
    icon: LocalShipping,
    metric: "1,284",
    metricLabel: "Orders managed",
    accent: "orange",
    preview: {
      label: "Delivery status",
      value: "86%",
      status: "On schedule",
    },
  },
  {
    id: 5,
    category: "ANALYTICS",
    title: "Turn Data Into Decisions",
    description:
      "Understand sales, inventory performance, profit and operational trends with actionable insights.",
    icon: Assessment,
    metric: "24/7",
    metricLabel: "Business insights",
    accent: "cyan",
    preview: {
      label: "Revenue trend",
      value: "+32.6%",
      status: "Growing",
    },
  },
  {
    id: 6,
    category: "UNI-CLOUD",
    title: "Your Business, Everywhere",
    description:
      "Keep your operations synchronized across locations and devices with cloud-powered inventory.",
    icon: CloudSync,
    metric: "99.9%",
    metricLabel: "Sync uptime",
    accent: "indigo",
    preview: {
      label: "Cloud sync",
      value: "LIVE",
      status: "All systems",
    },
  },
  {
    id: 7,
    category: "LIVE ALERTS",
    title: "Never Miss a Movement",
    description:
      "Stay informed about transactions, stock changes and important business activity in real time.",
    icon: NotificationsActive,
    metric: "LIVE",
    metricLabel: "Transaction feed",
    accent: "pink",
    preview: {
      label: "Latest activity",
      value: "Sale ₦24K",
      status: "Just now",
    },
  },
  {
    id: 8,
    category: "SECURITY",
    title: "Control Every Action",
    description:
      "Protect your business with roles, permissions and controlled access across your organization.",
    icon: Security,
    metric: "360°",
    metricLabel: "Access control",
    accent: "red",
    preview: {
      label: "Security",
      value: "ACTIVE",
      status: "Protected",
    },
  },
];

const FeatureCard = ({ feature }) => {
  const Icon = feature.icon;

  return (
    <article
      className={`feature-trailer__card feature-trailer__card--${feature.accent}`}
    >
      <div className="feature-trailer__card-glow" />

      <div className="feature-trailer__card-top">
        <div className="feature-trailer__icon">
          <Icon />
        </div>

        <span className="feature-trailer__number">
          {String(feature.id).padStart(2, "0")}
        </span>
      </div>

      <div className="feature-trailer__content">
        <span className="feature-trailer__category">
          {feature.category}
        </span>

        <h3>{feature.title}</h3>

        <p>{feature.description}</p>
      </div>

      <div className="feature-trailer__preview">
        <div className="feature-trailer__preview-header">
          <span>{feature.preview.label}</span>

          <span className="feature-trailer__live-dot">
            <i />
            LIVE
          </span>
        </div>

        <div className="feature-trailer__preview-body">
          <strong>{feature.preview.value}</strong>

          <span>{feature.preview.status}</span>
        </div>

        <div className="feature-trailer__progress">
          <span />
        </div>
      </div>

      <div className="feature-trailer__footer">
        <div>
          <strong>{feature.metric}</strong>
          <small>{feature.metricLabel}</small>
        </div>

        <button type="button" aria-label={`Explore ${feature.title}`}>
          <ArrowOutward />
        </button>
      </div>
    </article>
  );
};

const FeatureLane = ({ direction, items }) => {
  const duplicatedItems = [...items, ...items];

  return (
    <div
      className={`feature-trailer__lane feature-trailer__lane--${direction}`}
    >
      <div className="feature-trailer__track">
        {duplicatedItems.map((feature, index) => (
          <FeatureCard
            feature={feature}
            key={`${feature.id}-${direction}-${index}`}
          />
        ))}
      </div>
    </div>
  );
};

const FeatureTrailer = () => {
  return (
    <section className="feature-trailer">
      <div className="feature-trailer__background-grid" />

      <div className="feature-trailer__orb feature-trailer__orb--one" />
      <div className="feature-trailer__orb feature-trailer__orb--two" />

      <div className="feature-trailer__heading">
        <div className="feature-trailer__eyebrow">
          <span />
          BUILT FOR MODERN BUSINESS
          <span />
        </div>

        <h2>
          Everything your
          <strong> inventory needs.</strong>
        </h2>

        <p>
          One intelligent platform connecting inventory, sales, operations
          and business insights.
        </p>
      </div>

      <FeatureLane direction="right" items={FEATURES} />
      <FeatureLane direction="left" items={[...FEATURES].reverse()} />

      <div className="feature-trailer__bottom">
        <span>
          <TrendingUp />
          POWERING BETTER OPERATIONS
        </span>

        <span>
          <AutoGraph />
          BUILT TO SCALE
        </span>

        <span>
          <Storefront />
          ONE PLATFORM
        </span>
      </div>
    </section>
  );
};

export default FeatureTrailer;